import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { apiJson, ApiError } from '../api'
import type { ThemeTokens } from '../theme'
import { pageStyles } from '../styles'
import { DirectoryPicker, dirEmail } from '../components/DirectoryPicker'
import { GroupPicker, groupLabel } from '../components/GroupPicker'
import type { DirectoryGroup } from '../components/GroupPicker'
import { StatCard } from '../components/StatCard'

/** Matches `GET /api/models` in `backend/src/routes/models.ts`. */
type GrantView =
  | { state: 'ok'; groups: string[]; users: string[] }
  | { state: 'missing' | 'malformed' | 'error'; groups: []; users: [] }

interface ModelRow {
  alias: string
  slug: string
  label: string
  family: 'chat' | 'claude-code'
  offshore: boolean
  piiMasking: 'not-applicable' | 'masked' | 'unmasked'
  usageGroup: string
  grant: GrantView
}

interface UsageGroup {
  upstream: string
  aliases: string[]
  spend: number
  requests: number
}

interface ModelsResponse {
  models: ModelRow[]
  groupNames: Record<string, string>
  groupNamesError: string | null
  usage: {
    available: boolean
    ledger: string
    windowDays: number
    asOf: string | null
    groups: UsageGroup[]
    unattributed: Array<{ model: string; spend: number; requests: number }>
  }
}

/** Matches `GET /api/models/groups`. */
interface GroupChoice {
  id: string | null
  name: string
  label: string
  everyone: boolean
  error?: string
}

/** Matches `POST /api/models/impact`. */
interface Impact {
  removedCount: number
  addedCount: number
  beforeTotal: number
  afterTotal: number
  removed: Array<{ email: string; displayName: string }>
  added: Array<{ email: string; displayName: string }>
  basis: string
}

interface Draft {
  groups: string[]
  users: string[]
}

const FAMILY_TITLE: Record<ModelRow['family'], string> = {
  chat: 'Chat (LibreChat and Teams)',
  'claude-code': 'Claude Code (developer keys)',
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return `${err.message} (HTTP ${err.status})`
  return err instanceof Error ? err.message : String(err)
}

const money = (n: number) => `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const signature = (d: Draft) => JSON.stringify([[...d.groups].sort(), [...d.users].sort()])

/**
 * Models: who can use which model, and what it costs to be wrong.
 *
 * WHAT THIS PAGE WRITES IS WHAT THE GATEWAY ENFORCES. Each row is the SSM
 * grant `/chu-gpt/models/<slug>` that the LiteLLM guardrail reads, so a save
 * here is an access-control change. Two things follow, and the page is built
 * around them:
 *
 *   1. OFFSHORE IS NOT A LABEL, IT IS A DECISION. Four of these models send
 *      identifiers to the vendor unmasked (measured 25 Sep 2026, spec section
 *      1a). Those rows are drawn differently and their editor demands a reason.
 *   2. A SAVE IS PREVIEWED. The editor computes "this would remove access for
 *      N people" from live Entra before the Save button is live, and Save is
 *      disabled until the count on screen belongs to the draft on screen.
 *
 * USAGE IS SHOWN ONCE PER UPSTREAM MODEL, never per alias. The warehouse folds
 * `claude:sonnet-5`, `claude:sonnet-5-1m` and `claude-code:sonnet-5` into one
 * figure; the row that owns the figure shows it and the others point at it, so
 * nothing on this page can be summed into a number that is not real.
 */
export function ModelsPage({ tokens }: { tokens: ThemeTokens }) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])
  const [data, setData] = useState<ModelsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [notice, setNotice] = useState<{ text: string; warn: boolean } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await apiJson<ModelsResponse>('/api/models'))
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const models = data?.models ?? []
  const names = useMemo(() => {
    const m = new Map<string, string>()
    for (const [id, name] of Object.entries(data?.groupNames ?? {})) m.set(id.toLowerCase(), name)
    return m
  }, [data])
  const nameOf = (id: string) => names.get(id.toLowerCase()) ?? id

  // The usage figure belongs to the FIRST model (registry order) of its usage
  // group; every later model in that group points at it instead of repeating it.
  const usageOwner = useMemo(() => {
    const owner = new Map<string, string>()
    for (const m of models) if (!owner.has(m.usageGroup)) owner.set(m.usageGroup, m.alias)
    return owner
  }, [models])
  const usageByGroup = useMemo(() => new Map((data?.usage.groups ?? []).map((g) => [g.upstream, g])), [data])
  const labelOf = (alias: string) => models.find((m) => m.alias === alias)?.label ?? alias

  const unmasked = models.filter((m) => m.piiMasking === 'unmasked')
  const problems = models.filter((m) => m.grant.state !== 'ok')
  const editingModel = models.find((m) => m.alias === editing) ?? null

  const th: CSSProperties = {
    textAlign: 'left',
    padding: '0.4rem 0.7rem 0.4rem 0',
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    color: tokens.textSecondary,
    fontWeight: 700,
    borderBottom: `1px solid ${tokens.border}`,
    whiteSpace: 'nowrap',
  }
  const td: CSSProperties = {
    padding: '0.65rem 0.7rem 0.65rem 0',
    borderBottom: `1px solid ${tokens.border}`,
    verticalAlign: 'top',
    fontSize: '0.86rem',
    color: tokens.textPrimary,
  }
  const mono: CSSProperties = { fontFamily: "'DM Mono', ui-monospace, monospace", fontSize: '0.74rem', color: tokens.textSecondary }

  function whoCell(m: ModelRow) {
    if (m.grant.state !== 'ok') {
      const why: Record<string, string> = {
        missing: 'No grant exists. The gateway refuses this model to everyone.',
        malformed: 'The grant is malformed. The gateway refuses this model to everyone.',
        error: 'The grant could not be read just now. Its real state is unknown.',
      }
      return (
        <span style={{ color: tokens.danger, fontWeight: 600 }} role="alert">
          {why[m.grant.state]}
        </span>
      )
    }
    const { groups, users } = m.grant
    return (
      <div style={{ ...styles.chipRow, margin: 0 }}>
        {groups.map((g) => (
          <span key={g} style={{ ...styles.chip, paddingRight: '0.6rem' }} title={g}>
            {nameOf(g)}
          </span>
        ))}
        {users.length > 0 ? (
          <span style={{ ...styles.chip, paddingRight: '0.6rem' }} title={users.join(', ')}>
            +{users.length} {users.length === 1 ? 'person' : 'people'} directly
          </span>
        ) : null}
      </div>
    )
  }

  function usageCell(m: ModelRow) {
    if (!data?.usage.available) return <span style={styles.hint}>unavailable</span>
    const owner = usageOwner.get(m.usageGroup)
    const g = usageByGroup.get(m.usageGroup)
    if (owner !== m.alias) {
      return <span style={{ ...styles.hint, fontStyle: 'italic' }}>counted in {labelOf(owner ?? '')}</span>
    }
    if (!g) return <span style={styles.hint}>unavailable</span>
    const shared = g.aliases.filter((a) => a !== m.alias)
    return (
      <div>
        <div style={{ fontWeight: 600 }}>{money(g.spend)}</div>
        <div style={styles.hint}>{g.requests.toLocaleString('en-AU')} calls</div>
        {shared.length > 0 ? (
          <div style={{ ...styles.hint, fontStyle: 'italic' }} title={g.aliases.join(', ')}>
            shared with {shared.length} other alias{shared.length === 1 ? '' : 'es'}
          </div>
        ) : null}
      </div>
    )
  }

  const familyTable = (family: ModelRow['family']) => (
    <div key={family} style={{ overflowX: 'auto' }}>
      <h3 style={{ ...styles.fieldLabel, margin: '1.2rem 0 0.3rem' }}>{FAMILY_TITLE[family]}</h3>
      {/* Fixed layout with shared widths, so the Chat and Claude Code tables
          line up column for column instead of each sizing to its own content. */}
      <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '27%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '30%' }} />
          <col style={{ width: '17%' }} />
          <col style={{ width: '10%' }} />
        </colgroup>
        <thead>
          <tr>
            <th style={th}>Model</th>
            <th style={th}>Runs</th>
            <th style={th}>Who can use it</th>
            <th style={th}>Last {data?.usage.windowDays ?? 30} days</th>
            <th style={th} />
          </tr>
        </thead>
        <tbody>
          {models
            .filter((m) => m.family === family)
            .map((m) => {
              const bad = m.piiMasking === 'unmasked'
              return (
                <tr key={m.alias} aria-label={m.label} style={bad ? { background: tokens.surfaceRaised } : undefined}>
                  <td style={{ ...td, borderLeft: `4px solid ${bad ? tokens.danger : 'transparent'}`, paddingLeft: '0.7rem' }}>
                    <div style={{ fontWeight: 600 }}>{m.label}</div>
                    <div style={mono}>{m.alias}</div>
                  </td>
                  <td style={td}>
                    {m.offshore ? (
                      <>
                        <span style={{ ...styles.badge, background: tokens.danger, color: tokens.dangerContrast }}>Offshore</span>
                        <div
                          style={{
                            marginTop: '0.3rem',
                            fontSize: '0.78rem',
                            fontWeight: bad ? 700 : 500,
                            color: bad ? tokens.danger : tokens.textSecondary,
                          }}
                        >
                          {bad ? 'Identifiers sent unmasked' : 'Identifiers masked'}
                        </div>
                      </>
                    ) : (
                      <span style={{ ...styles.badge, background: tokens.surfaceRaised, color: tokens.textSecondary, border: `1px solid ${tokens.border}` }}>
                        Australia
                      </span>
                    )}
                  </td>
                  <td style={td}>{whoCell(m)}</td>
                  <td style={td}>{usageCell(m)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button
                      type="button"
                      style={styles.buttonGhost}
                      aria-label={`Edit access to ${m.label}`}
                      onClick={() => setEditing(m.alias)}
                    >
                      Edit access
                    </button>
                  </td>
                </tr>
              )
            })}
        </tbody>
      </table>
    </div>
  )

  return (
    <div style={{ ...styles.page, maxWidth: 'none' }}>
      <div style={styles.callout} role="note">
        <strong>Each row is the grant the gateway enforces.</strong>
        <span>
          Stored at <code>/chu-gpt/models/&lt;model&gt;</code>. A change reaches the gateway within about a minute. Whether
          the gateway refuses or only logs a request against these grants is the guardrail&apos;s mode in terraform, set
          outside this page. Claude Desktop cannot be restricted here: its model is chosen inside Anthropic&apos;s own
          client and never touches our gateway.
        </span>
      </div>

      {unmasked.length > 0 ? (
        <div style={styles.warningCallout} role="alert">
          {unmasked.length} of these models send personal identifiers to the vendor UNMASKED:{' '}
          {unmasked.map((m) => m.label.replace(/ \(.*\)$/, '')).join(', ')}. Anyone granted one is sending names, emails
          and phone numbers offshore as typed. Who may use them is a data-residency decision. Measured with synthetic
          data on 25 Sep 2026; masking is not yet fixed.
        </div>
      ) : null}

      <div style={styles.statsRow}>
        <StatCard label="Models governed" value={models.length} tokens={tokens} />
        <StatCard
          label="Offshore"
          value={models.filter((m) => m.offshore).length}
          hint={`${unmasked.length} unmasked`}
          tokens={tokens}
          tone={unmasked.length > 0 ? 'danger' : 'default'}
        />
        <StatCard
          label="Refused to everyone"
          value={problems.length}
          hint="missing, malformed or unreadable grant"
          tokens={tokens}
          tone={problems.length > 0 ? 'danger' : 'default'}
        />
      </div>

      {error ? <p style={styles.error}>{error}</p> : null}
      {notice ? (
        <p style={notice.warn ? styles.error : styles.hint} role="status">
          {notice.text}
        </p>
      ) : null}
      {data?.groupNamesError ? (
        <p style={styles.error}>
          Group names could not be looked up, so groups are shown by their id: {data.groupNamesError}
        </p>
      ) : null}
      {loading && !data ? <p style={styles.hint}>Loading models…</p> : null}

      {data ? (
        <section style={styles.section} aria-label="Models">
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Who can use which model</h2>
            <button type="button" style={styles.buttonGhost} onClick={() => void load()} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          {familyTable('chat')}
          {familyTable('claude-code')}
          <p style={{ ...styles.hint, marginTop: '1rem' }}>
            {data.usage.available ? (
              <>
                Usage: {data.usage.ledger}, last {data.usage.windowDays} days
                {data.usage.asOf ? `, through ${data.usage.asOf}` : ''}. It is recorded per upstream model, not per alias,
                so aliases that share one model share one figure and it is shown once. Do not add the rows up.
              </>
            ) : (
              <>Usage could not be read from the warehouse just now, so it is shown as unavailable, not zero.</>
            )}
          </p>
          {data.usage.unattributed.length > 0 ? (
            <p style={styles.error}>
              Spend on models this list does not govern:{' '}
              {data.usage.unattributed.map((u) => `${u.model} ${money(u.spend)}`).join(', ')}. An unlisted model is an
              ungoverned one.
            </p>
          ) : null}
        </section>
      ) : null}

      {editingModel ? (
        <ModelEditor
          key={editingModel.alias}
          tokens={tokens}
          model={editingModel}
          nameOf={nameOf}
          onClose={() => setEditing(null)}
          onSaved={async (text, warn) => {
            setEditing(null)
            setNotice({ text, warn })
            await load()
          }}
          onStale={() => void load()}
        />
      ) : null}
    </div>
  )
}

/**
 * The editor for one model's grant. Draft state is local; nothing is written
 * until Save, and Save is live only when the impact on screen was computed for
 * the draft on screen (`impactFor === signature(draft)`), so a fast click
 * cannot save a grant the operator has not seen the consequence of.
 */
export function ModelEditor({
  tokens,
  model,
  nameOf,
  onClose,
  onSaved,
  onStale,
}: {
  tokens: ThemeTokens
  model: ModelRow
  nameOf: (id: string) => string
  onClose: () => void
  onSaved: (text: string, warn: boolean) => void | Promise<void>
  onStale: () => void
}) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])
  const seen: Draft | null = model.grant.state === 'ok' ? { groups: model.grant.groups, users: model.grant.users } : null
  const [draft, setDraft] = useState<Draft>(() => ({
    groups: seen ? seen.groups.map((g) => g.toLowerCase()) : [],
    users: seen ? [...seen.users] : [],
  }))
  const [reason, setReason] = useState('')
  /** The operator declaring that an empty grant is DELIBERATE: this model is
   *  being withdrawn from everyone. Kept separate from `empty` because an
   *  empty draft and an intentional withdrawal look identical, and only one
   *  of them should be one click from turning a model off for the company. */
  const [turnOff, setTurnOff] = useState(false)
  const [choices, setChoices] = useState<GroupChoice[] | null>(null)
  const [choicesError, setChoicesError] = useState<string | null>(null)
  const [extraNames, setExtraNames] = useState<Record<string, string>>({})
  const [impact, setImpact] = useState<Impact | null>(null)
  const [impactFor, setImpactFor] = useState<string | null>(null)
  const [impactError, setImpactError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [showRemoved, setShowRemoved] = useState(false)
  const reqId = useRef(0)

  const sig = signature(draft)
  const unchanged = seen !== null && sig === signature({ groups: seen.groups.map((g) => g.toLowerCase()), users: seen.users })
  const empty = draft.groups.length === 0 && draft.users.length === 0
  // Ticking anything is a change of mind; the declaration must not survive it.
  if (!empty && turnOff) setTurnOff(false)
  const everyone = choices?.find((c) => c.everyone)
  const needsReason = model.offshore

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const r = await apiJson<{ groups: GroupChoice[] }>('/api/models/groups')
        if (!cancelled) setChoices(r.groups)
      } catch (err) {
        if (!cancelled) setChoicesError(errorMessage(err))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // The live impact, recomputed (debounced) whenever the draft changes. Each
  // request carries a number; an answer for anything but the latest is dropped.
  useEffect(() => {
    // An empty draft normally has no impact worth computing, because it
    // cannot be saved. A DECLARED turn-off can be, and its impact is the
    // whole point: everyone who can use the model today is about to lose it,
    // and that count belongs on the button before it is pressed.
    if ((empty && !turnOff) || unchanged) {
      setImpact(null)
      setImpactFor(null)
      setImpactError(null)
      return
    }
    const mine = (reqId.current += 1)
    setImpactError(null)
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const r = await apiJson<Impact>('/api/models/impact', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // A declared turn-off must say so: the route 400s an empty grant
            // without it, which left the Turn off button disabled for good
            // (Maroun, 25 Sep 2026: "nothing happens").
            body: JSON.stringify({
              alias: model.alias,
              groups: draft.groups,
              users: draft.users,
              ...(turnOff ? { turnOff: true } : {}),
            }),
          })
          if (reqId.current !== mine) return
          setImpact(r)
          setImpactFor(sig)
        } catch (err) {
          if (reqId.current !== mine) return
          setImpact(null)
          setImpactFor(null)
          setImpactError(errorMessage(err))
        }
      })()
    }, 350)
    return () => clearTimeout(timer)
    // `sig` captures draft; model.alias is fixed for this editor's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, empty, turnOff, unchanged])

  const toggleGroup = (id: string) =>
    setDraft((d) => ({
      ...d,
      groups: d.groups.includes(id) ? d.groups.filter((g) => g !== id) : [...d.groups, id],
    }))

  // Groups on the draft that are not one of the checkbox choices (added via the
  // search box, or already on the grant): listed so they can be unticked.
  const choiceIds = new Set((choices ?? []).flatMap((c) => (c.id ? [c.id] : [])))
  const otherGroups = draft.groups.filter((g) => !choiceIds.has(g))
  const displayName = (id: string) => extraNames[id] ?? nameOf(id)

  const impactCurrent = impactFor === sig && impact !== null
  const removed = impactCurrent ? impact.removedCount : 0
  // An empty draft blocks the save UNLESS the operator has declared the
  // withdrawal (`turnOff`). A reason is always required to turn a model off,
  // whether or not the model is offshore: "who took this away and why" is the
  // question somebody asks a week later.
  const canSave =
    !saving &&
    (!empty || turnOff) &&
    !unchanged &&
    impactCurrent &&
    (!(needsReason || turnOff) || reason.trim().length > 0)

  async function save() {
    setSaving(true)
    setSaveError(null)
    try {
      const r = await apiJson<{ changed: boolean; auditWarning?: string }>('/api/models/grant', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          alias: model.alias,
          groups: draft.groups,
          users: draft.users,
          basedOn: seen,
          ...(turnOff ? { turnOff: true } : {}),
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        }),
      })
      await onSaved(
        r.changed
          ? `Saved ${model.label}. The gateway picks it up within about a minute.${r.auditWarning ? ` ${r.auditWarning}` : ''}`
          : `${model.label} was already set that way. Nothing changed.`,
        Boolean(r.auditWarning),
      )
    } catch (err) {
      setSaveError(errorMessage(err))
      if (err instanceof ApiError && err.status === 409) onStale()
    } finally {
      setSaving(false)
    }
  }

  const unmasked = model.piiMasking === 'unmasked'

  return (
    <div style={styles.modalOverlay} role="presentation" onClick={() => !saving && onClose()}>
      <div
        style={{ ...styles.modalCard, maxWidth: '38rem' }}
        role="dialog"
        aria-modal="true"
        aria-label={`Access to ${model.label}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={styles.modalTitle}>Access to {model.label}</h3>
        <div style={{ ...styles.hint, marginBottom: '0.8rem', fontFamily: "'DM Mono', ui-monospace, monospace" }}>{model.alias}</div>

        {model.offshore ? (
          <div style={{ ...styles.warningCallout, marginBottom: '1rem' }} role="alert">
            {unmasked
              ? 'This model runs outside Australia and sends identifiers to the vendor UNMASKED. Granting it lets those people send names, emails and phone numbers offshore as typed. '
              : 'This model runs outside Australia. Identifiers are masked on the way out. '}
            A reason is required and is written to the audit log.
          </div>
        ) : null}

        {model.grant.state !== 'ok' ? (
          <div style={{ ...styles.warningCallout, marginBottom: '1rem' }} role="alert">
            This model has no valid grant, so the gateway refuses it to everyone. Saving creates one.
          </div>
        ) : null}

        <div style={styles.field}>
          <span style={styles.fieldLabel}>Groups</span>
          {choicesError ? <p style={styles.error}>Could not load the group list ({choicesError}). You can still search for a group below.</p> : null}
          {choices === null && !choicesError ? <p style={styles.hint}>Loading groups…</p> : null}
          <div style={styles.checkboxGrid}>
            {(choices ?? []).map((c) => (
              <label key={c.name} style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  disabled={c.id === null || saving}
                  checked={c.id !== null && draft.groups.includes(c.id)}
                  onChange={() => c.id && toggleGroup(c.id)}
                />
                <span>
                  {c.everyone ? 'Everyone' : c.name}
                  {c.everyone ? <span style={styles.hint}> ({c.name}, all staff)</span> : null}
                  {c.id === null ? <span style={{ color: tokens.danger }}> not found in Entra</span> : null}
                </span>
              </label>
            ))}
            {otherGroups.map((g) => (
              <label key={g} style={styles.checkboxRow}>
                <input type="checkbox" checked disabled={saving} onChange={() => toggleGroup(g)} />
                <span>{displayName(g)}</span>
              </label>
            ))}
          </div>
          <div style={{ marginTop: '0.5rem' }}>
            <GroupPicker
              tokens={tokens}
              disabled={saving}
              placeholder="Add another Entra group…"
              excludeIds={draft.groups}
              onPick={(g: DirectoryGroup) => {
                const id = g.id.toLowerCase()
                setExtraNames((n) => ({ ...n, [id]: groupLabel(g) }))
                setDraft((d) => (d.groups.includes(id) ? d : { ...d, groups: [...d.groups, id] }))
              }}
            />
          </div>
        </div>

        <div style={styles.field}>
          <span style={styles.fieldLabel}>People, directly ({draft.users.length})</span>
          <div style={styles.chipRow}>
            {draft.users.map((u) => (
              <span key={u} style={styles.chip}>
                {u}
                <button
                  type="button"
                  style={styles.chipRemove}
                  aria-label={`Remove ${u}`}
                  disabled={saving}
                  onClick={() => setDraft((d) => ({ ...d, users: d.users.filter((x) => x !== u) }))}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div style={{ marginTop: '0.4rem' }}>
            <DirectoryPicker
              tokens={tokens}
              disabled={saving}
              placeholder="Add a person…"
              onPick={(u) => {
                const email = dirEmail(u).toLowerCase()
                if (email) setDraft((d) => (d.users.includes(email) ? d : { ...d, users: [...d.users, email] }))
              }}
            />
          </div>
          <span style={styles.hint}>Direct grants are exceptions. Prefer a group.</span>
        </div>

        {/* THE IMPACT. Always present, always says which state it is in. */}
        <div
          style={{
            ...styles.callout,
            marginBottom: '1rem',
            borderColor: removed > 0 || impactError ? tokens.danger : tokens.border,
          }}
          role="status"
          aria-live="polite"
        >
          {empty ? (
            <span style={{ color: tokens.danger, fontWeight: 600 }}>
              {turnOff ? (
                <>
                  This model will be turned OFF. Nobody will be able to use it, and the gateway
                  will refuse it within about a minute.{' '}
                  <button type="button" style={styles.buttonGhost} onClick={() => setTurnOff(false)}>
                    Undo
                  </button>
                  {impactError ? (
                    // Shown here too: this branch sits above the general
                    // error branch, which is how a failed preview once
                    // disabled the button without a word.
                    <span style={{ display: 'block', marginTop: '0.4rem' }}>
                      Could not work out who this affects, so saving is off: {impactError}
                    </span>
                  ) : null}
                </>
              ) : (
                <>
                  Nobody is selected. Pick who may use it, or turn the model off for everyone.
                  {everyone?.id ? (
                    <>
                      {' '}
                      <button type="button" style={styles.buttonGhost} onClick={() => everyone.id && setDraft((d) => ({ ...d, groups: [everyone.id as string] }))}>
                        Select Everyone
                      </button>
                    </>
                  ) : null}{' '}
                  <button type="button" style={styles.buttonGhost} onClick={() => setTurnOff(true)}>
                    Turn this model off
                  </button>
                </>
              )}
            </span>
          ) : unchanged ? (
            <span>No change from the current grant.</span>
          ) : impactError ? (
            <span style={{ color: tokens.danger, fontWeight: 600 }}>
              Could not work out who this affects, so saving is off: {impactError}
            </span>
          ) : !impactCurrent ? (
            <span>Working out who this affects…</span>
          ) : (
            <>
              <strong style={{ color: impact.removedCount > 0 ? tokens.danger : tokens.textPrimary, fontSize: '0.95rem' }}>
                {impact.removedCount === 0
                  ? 'This removes access for nobody.'
                  : `This would remove access for ${impact.removedCount} ${impact.removedCount === 1 ? 'person' : 'people'}.`}
              </strong>
              <span>
                {impact.addedCount > 0 ? `It gives access to ${impact.addedCount} more. ` : ''}
                {impact.beforeTotal} can use it now; {impact.afterTotal} would after. {impact.basis}
              </span>
              {impact.removedCount > 0 ? (
                <>
                  <button
                    type="button"
                    style={{ ...styles.buttonGhost, alignSelf: 'flex-start' }}
                    onClick={() => setShowRemoved((v) => !v)}
                  >
                    {showRemoved ? 'Hide who' : 'Show who'}
                  </button>
                  {showRemoved ? (
                    <div style={{ maxHeight: '9rem', overflowY: 'auto', fontSize: '0.8rem' }}>
                      {impact.removed.map((p) => (
                        <div key={p.email}>
                          {p.displayName ? `${p.displayName}, ` : ''}
                          {p.email}
                        </div>
                      ))}
                      {impact.removedCount > impact.removed.length ? (
                        <div style={styles.hint}>…and {impact.removedCount - impact.removed.length} more.</div>
                      ) : null}
                    </div>
                  ) : null}
                </>
              ) : null}
            </>
          )}
        </div>

        <div style={styles.field}>
          <label style={styles.fieldLabel} htmlFor="model-reason">
            Reason {needsReason ? '(required)' : '(optional)'}
          </label>
          <textarea
            id="model-reason"
            style={styles.textarea}
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={saving}
            placeholder={needsReason ? 'Why should these people be able to use an offshore model?' : 'Why is this change needed?'}
          />
        </div>

        {saveError ? <p style={styles.error}>{saveError}</p> : null}
        <div style={styles.modalActions}>
          <button type="button" style={styles.buttonGhost} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            style={removed > 0 || turnOff ? styles.buttonDanger : styles.button}
            disabled={!canSave}
            onClick={() => void save()}
          >
            {saving
              ? 'Saving…'
              : turnOff
                ? `Turn off ${model.label}${removed > 0 ? `, remove access for ${removed}` : ''}`
                : removed > 0
                  ? `Save, remove access for ${removed}`
                  : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ModelsPage
